import { logError } from '../lib/logger';
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { getHistory } from '../lib/api';
import { MagnifyingGlass, ClockCounterClockwise, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { StatusChip, PageHeader } from './enterprise/ui';

export default function History() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    try { setItems(await getHistory()); }
    catch (e) { logError('History load error:', e); toast.error('Failed to load history'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = items.filter(i =>
    i.borrower.toLowerCase().includes(search.toLowerCase()) ||
    i.sa_initials.toLowerCase().includes(search.toLowerCase()) ||
    (i.notes || '').toLowerCase().includes(search.toLowerCase()) ||
    (i.loan_officer || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-4" data-testid="history-view">
      <PageHeader
        title="History"
        subtitle={`${items.length} completed file${items.length !== 1 ? 's' : ''} — the audit trail of every closed loan.`}
        actions={
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search history..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-64 h-9 text-sm border-border bg-card" data-testid="search-history" />
          </div>
        }
      />

      <Card className="border border-border bg-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em] pl-4">Borrower</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">SA</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Loan Officer</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Type</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Urgency</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Notes</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Closing</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em] pr-4">Completed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                  <ClockCounterClockwise size={28} className="mx-auto mb-2 text-muted-foreground/50" />
                  {items.length === 0 ? 'No completed files yet. Files move here when marked complete.' : 'No matches found.'}
                </TableCell></TableRow>
              ) : filtered.map(item => (
                <TableRow key={item.id} className="border-border hover:bg-muted/30" data-testid={`history-row-${item.id}`}>
                  <TableCell className="pl-4 font-medium text-foreground text-sm">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle size={14} weight="fill" className="text-emerald-500 flex-shrink-0" />
                      {item.borrower}
                    </div>
                  </TableCell>
                  <TableCell><span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{item.sa_initials}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{item.loan_officer || '-'}</TableCell>
                  <TableCell>
                    <StatusChip tone={item.task_type === 'Escalation' ? 'critical' : 'info'}>{item.task_type || 'Submission'}</StatusChip>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{item.urgency}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{item.notes || '-'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{item.closing_date || '-'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground pr-4">{new Date(item.completed_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
